import React from "react";
import { Phone, ShieldCheck, MapPin, Award, Users, PhoneOff, AlertTriangle, Home, Sparkles, BadgeCheck, DollarSign, ArrowRight, CreditCard, Banknote } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "../lib/site";

const TRUST_CHIPS = [
  { icon: Award, label: "25+ yrs cyber security" },
  { icon: MapPin, label: "Adelaide local" },
  { icon: PhoneOff, label: "No overseas call centres" },
  { icon: ShieldCheck, label: "Scam & ID protection" },
  { icon: BadgeCheck, label: "EFTPOS or cash on completion" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white"
    >
      {/* Decorative background */}
      <div aria-hidden="true" className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5FAEC] via-white to-[#F0F7E8]" />
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#6BAF3C]/15 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#0F2E52]/10 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-8 pt-3 md:pt-8 pb-8 md:pb-14">
        <div className="grid md:grid-cols-12 gap-4 md:gap-12 items-center">
          {/* Left: copy + scam CTA — text FIRST on mobile now */}
          <div className="md:col-span-7 order-1 fade-up">
            {/* Mobile-only: side-by-side headline + compact photo card */}
            <div className="md:hidden flex gap-3 items-start mb-3">
              <div className="flex-1 min-w-0">
                {/* Adelaide local badge (mobile) */}
                <span className="inline-flex items-center gap-1.5 bg-[#6BAF3C] text-white font-[Work_Sans] font-black text-[10px] uppercase tracking-widest rounded-full px-3 py-1 shadow-sm whitespace-nowrap">
                  <MapPin className="w-3 h-3" strokeWidth={2.8} />
                  Adelaide Local · Visiting You
                </span>
                <h1 className="mt-2 font-[Work_Sans] font-black tracking-tight text-[#0F2E52] leading-[0.98] text-[26px] xs:text-3xl">
                  Technology doesn’t have to be{" "}
                  <span className="text-[#6BAF3C]">confusing</span>.
                </h1>

                {/* myGov / myID callout — fills whitespace under headline */}
                <a
                  href="#services"
                  data-testid="hero-mygov-callout-mobile"
                  className="mt-3 flex items-stretch gap-1.5 max-w-full"
                >
                  <div className="flex items-stretch gap-1 shrink-0">
                    <img
                      src="https://customer-assets.emergentagent.com/job_patient-tech-help/artifacts/6b54tz5k_myid.jpeg"
                      alt="myID logo"
                      className="h-10 w-auto rounded-md object-contain border border-[#DCE4EC] shadow-sm bg-white"
                    />
                    <img
                      src="https://customer-assets.emergentagent.com/job_patient-tech-help/artifacts/z3if5ena_mygovID.jpeg"
                      alt="myGovID logo"
                      className="h-10 w-auto rounded-md object-contain border border-[#DCE4EC] shadow-sm bg-white"
                    />
                  </div>
                  <div className="flex-1 min-w-0 bg-[#6BAF3C] text-white rounded-lg px-2 py-1.5 shadow-md flex items-center justify-center text-center">
                    <span className="font-[Work_Sans] font-black uppercase tracking-wide text-[9px] leading-tight">
                      Trouble with myGov?
                      <br />
                      We can help.
                    </span>
                  </div>
                </a>
              </div>

              {/* Compact photo card — mobile-only (no WE COME TO YOU circle) */}
              <div className="w-[132px] shrink-0 relative">
                <div className="relative rounded-2xl overflow-hidden ring-2 ring-white shadow-lg aspect-[3/4]">
                  <img
                    src={SITE.ownerPhoto}
                    alt="Tom — The Patient Tech Bloke"
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#0F2E52]/25 via-transparent to-transparent" />
                  <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 bg-white/95 rounded-full px-1.5 py-0.5 text-[9px] font-semibold text-[#0F2E52]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#6BAF3C]" />
                    Available
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop-only Adelaide local badge */}
            <div className="hidden md:flex justify-start mb-4">
              <span
                data-testid="adelaide-local-badge"
                className="inline-flex items-center gap-2 bg-[#6BAF3C] text-white font-[Work_Sans] font-black text-xs md:text-sm uppercase tracking-widest rounded-full px-4 py-1.5 shadow-sm"
              >
                <MapPin className="w-3.5 h-3.5" strokeWidth={2.8} />
                Adelaide Local · Visiting You
              </span>
            </div>

            {/* Desktop headline */}
            <h1 className="hidden md:block font-[Work_Sans] font-black tracking-tight text-[#0F2E52] leading-[1.02] text-left md:text-5xl lg:text-6xl">
              Technology doesn’t have <br className="hidden lg:block" />
              to be <span className="text-[#6BAF3C]">confusing</span>.
            </h1>

            <p className="mt-3 md:mt-4 text-base md:text-xl text-[#334155] leading-snug md:leading-relaxed max-w-xl md:mx-0">
              Friendly, patient help for{" "}
              <strong className="text-[#0F2E52]">Adelaide seniors</strong>{" "}
              in the comfort of your own home — from the{" "}
              <span className="font-semibold text-[#0F2E52]">cyber-security bloke</span>{" "}
              with 25+ years keeping Aussies safe.
            </p>

            {/* Unified scam hook + phone CTA */}
            <a
              href={`tel:${SITE.phoneTel}`}
              data-testid="hero-call-button"
              className="mt-4 md:mt-5 group relative block overflow-hidden rounded-2xl shadow-xl shadow-[#0F2E52]/25 transition-transform hover:-translate-y-1 focus:-translate-y-1 max-w-md mx-auto md:mx-0"
            >
              {/* Red hook strip */}
              <div className="bg-[#CC0000] px-4 py-2.5 flex items-center gap-2.5">
                <AlertTriangle className="w-5 h-5 text-white shrink-0" strokeWidth={2.5} />
                <span className="leading-tight">
                  <span className="block font-[Work_Sans] font-black text-white text-base md:text-lg uppercase tracking-wide">
                    Don’t fall for scams · <span className="text-[#FFD700]">FREE CALL</span>
                  </span>
                  <span className="block text-white/95 text-xs md:text-sm font-semibold">
                    Check with us first! Better safe than scammed.
                  </span>
                </span>
              </div>
              {/* Navy phone strip */}
              <div className="bg-[#0F2E52] group-hover:bg-[#0A2340] transition-colors px-4 py-3.5 md:py-4 flex items-center gap-3">
                <span className="relative w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/15 grid place-items-center shrink-0 group-hover:scale-105 transition-transform">
                  <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={2.5} />
                  <span className="absolute inset-0 rounded-full ring-4 ring-white/25 animate-pulse pointer-events-none" />
                </span>
                <span className="text-left leading-tight text-white flex-1 min-w-0">
                  <span className="block font-[Work_Sans] font-black text-xl md:text-2xl tracking-tight whitespace-nowrap">
                    {SITE.phone}
                  </span>
                  <span className="mt-0.5 inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold text-white/95">
                    <Home className="w-3.5 h-3.5 text-[#6BAF3C]" strokeWidth={2.5} />
                    Call to book a home visit — I come to you
                  </span>
                </span>
              </div>
            </a>

            {/* View Pricing button — sits right under the primary Call CTA, visible on mobile + desktop */}
            <Link
              to="/pricing"
              data-testid="hero-view-pricing"
              className="mt-3 group relative flex items-center justify-center gap-3 max-w-md mx-auto md:mx-0 rounded-2xl bg-white border-2 border-[#0F2E52] hover:bg-[#F0F7E8] focus:bg-[#F0F7E8] text-[#0F2E52] font-bold px-5 py-3.5 md:py-4 shadow-sm hover:shadow-md transition-all"
            >
              <span className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#F0F7E8] border border-[#C7DDA6] grid place-items-center shrink-0">
                <DollarSign className="w-5 h-5 md:w-5 md:h-5 text-[#6BAF3C]" strokeWidth={2.6} />
              </span>
              <span className="flex-1 text-left leading-tight">
                <span className="block font-[Work_Sans] font-black text-lg md:text-xl tracking-tight">
                  View Pricing
                </span>
                <span className="block text-xs md:text-sm font-semibold text-[#475569]">
                  $139 first hour · Clear &amp; upfront
                </span>
              </span>
              <ArrowRight className="w-5 h-5 text-[#0F2E52] group-hover:translate-x-0.5 transition-transform" strokeWidth={2.6} />
            </Link>

            {/* EFTPOS or Cash payment strip — prominent on homepage */}
            <div
              data-testid="hero-payment-strip"
              className="mt-3 max-w-md mx-auto md:mx-0 rounded-2xl bg-[#F0F7E8] border-2 border-[#6BAF3C] px-4 py-3 md:py-3.5 flex items-center gap-3 shadow-sm"
            >
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white border border-[#C7DDA6] grid place-items-center shadow-sm">
                  <CreditCard className="w-5 h-5 text-[#0F2E52]" strokeWidth={2.4} />
                </span>
                <span className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white border border-[#C7DDA6] grid place-items-center shadow-sm">
                  <Banknote className="w-5 h-5 text-[#0F2E52]" strokeWidth={2.4} />
                </span>
              </div>
              <div className="flex-1 min-w-0 leading-tight">
                <span className="block font-[Work_Sans] font-black text-[#0F2E52] text-base md:text-lg tracking-tight">
                  EFTPOS or Cash
                </span>
                <span className="block text-xs md:text-sm font-semibold text-[#334155]">
                  Paid on completion of the job
                </span>
              </div>
            </div>

            <p className="mt-3 text-sm md:text-base font-bold text-[#0F2E52] text-center md:text-left">
              <span className="whitespace-nowrap">You speak to Tom</span>{" "}
              <span className="whitespace-nowrap">· No overseas call centres</span>{" "}
              <span className="whitespace-nowrap">· Free 5-minute chat</span>
            </p>

            {/* Signature tagline */}
            <div className="mt-5 flex items-baseline gap-2 justify-center md:justify-start">
              <span className="text-sm md:text-base font-semibold text-[#0F2E52]">
                No jargon. No rush.
              </span>
              <span className="font-script text-[#6BAF3C] text-2xl md:text-3xl leading-none">
                <span className="hand-underline">Just Tom.</span>
              </span>
            </div>

            {/* Trust chips */}
            <ul className="mt-5 flex flex-wrap gap-2 justify-center md:justify-start">
              {TRUST_CHIPS.map((c) => {
                const Icon = c.icon;
                return (
                  <li
                    key={c.label}
                    className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#DCE4EC] rounded-full px-3 py-1.5 text-sm font-semibold text-[#0F2E52]"
                  >
                    <Icon className="w-4 h-4 text-[#6BAF3C] shrink-0" strokeWidth={2.5} />
                    <span>{c.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right: portrait — desktop only (mobile has compact split above) */}
          <div className="md:col-span-5 order-2 relative fade-up hidden md:block">
            {/* Desktop-only myGov / myID help callout ABOVE the photo */}
            <a
              href="#services"
              data-testid="hero-mygov-callout"
              className="hidden md:flex items-stretch gap-3 mb-4 max-w-md hover:opacity-95 transition-opacity"
            >
              <div className="flex items-stretch gap-2 shrink-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_patient-tech-help/artifacts/6b54tz5k_myid.jpeg"
                  alt="myID logo"
                  className="h-14 w-auto rounded-lg object-contain border border-[#DCE4EC] shadow-sm bg-white"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_patient-tech-help/artifacts/z3if5ena_mygovID.jpeg"
                  alt="myGovID logo"
                  className="h-14 w-auto rounded-lg object-contain border border-[#DCE4EC] shadow-sm bg-white"
                />
              </div>
              <div className="flex-1 bg-[#6BAF3C] text-white rounded-2xl px-4 py-3 shadow-md flex items-center justify-center text-center">
                <span className="font-[Work_Sans] font-black uppercase tracking-wide text-sm lg:text-base leading-tight">
                  Having trouble with myGov?
                  <br />
                  We can help.
                </span>
              </div>
            </a>
            <div aria-hidden="true" className="absolute inset-0 -z-10 flex items-center justify-center">
              <div className="w-[90%] aspect-square rounded-full bg-gradient-to-br from-[#6BAF3C]/20 via-transparent to-[#0F2E52]/15 blur-2xl" />
            </div>

            {/* Photo container */}
            <div className="relative mx-auto max-w-[260px] md:max-w-[380px] mt-4">
              {/* Photo */}
              <div className="relative rounded-3xl overflow-hidden ring-4 ring-white shadow-2xl shadow-[#0F2E52]/15 aspect-[4/5]">
                <img
                  src={SITE.ownerPhoto}
                  alt="Tom — The Patient Tech Bloke, Adelaide"
                  data-testid="hero-image"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#0F2E52]/20 via-transparent to-transparent" />

                {/* Small "Available today" badge — bottom-left, away from face */}
                <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-white/95 border border-[#DCE4EC] rounded-full px-3 py-1.5 text-xs md:text-sm font-semibold text-[#0F2E52]">
                  <span className="w-2 h-2 rounded-full bg-[#6BAF3C]" />
                  Available today
                </span>
              </div>

              {/* "WE COME TO YOU!" — bottom-right, outside photo */}
              <div className="absolute -bottom-4 -right-3 md:-right-6 w-20 h-20 md:w-24 md:h-24 bg-[#6BAF3C] text-white rounded-full grid place-items-center shadow-xl text-center -rotate-6 border-4 border-white">
                <div>
                  <div className="text-[10px] md:text-xs font-[Work_Sans] font-black uppercase leading-none">
                    We come
                  </div>
                  <div className="text-[10px] md:text-xs font-[Work_Sans] font-black uppercase leading-none mt-0.5">
                    to you!
                  </div>
                </div>
              </div>
            </div>

            {/* Stats row under photo (desktop only) */}
            <div className="hidden md:grid grid-cols-3 gap-3 mt-8">
              <Stat icon={Award} value="25+" label="Years" />
              <Stat icon={Users} value="500+" label="Locals helped" />
              <Stat icon={ShieldCheck} value="100%" label="Scam-safe" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="text-center bg-white border border-[#DCE4EC] rounded-xl py-2.5">
      <Icon className="w-4 h-4 text-[#6BAF3C] mx-auto" strokeWidth={2.2} />
      <div className="mt-0.5 font-[Work_Sans] font-bold text-lg text-[#0F2E52] leading-none">
        {value}
      </div>
      <div className="text-xs text-[#475569] mt-0.5">{label}</div>
    </div>
  );
}
